export interface ErrorDetails {
  message: string;
  type: 'error' | 'warning' | 'info' | 'success';
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export interface ApiError extends Error {
  status?: number;
  code?: string;
  details?: any;
}

export class ErrorHandler {
  private static toastContainer: HTMLElement | null = null;

  // Initialize toast container
  static init() {
    if (typeof window === 'undefined') return;
    
    if (!this.toastContainer) {
      this.toastContainer = document.createElement('div');
      this.toastContainer.id = 'toast-container';
      this.toastContainer.className = 'fixed top-4 right-4 z-50 space-y-2';
      document.body.appendChild(this.toastContainer);
    }
  }

  // Show toast notification
  static showToast(details: ErrorDetails) {
    this.init();
    if (!this.toastContainer) return;

    const toast = document.createElement('div');
    const toastId = `toast-${Date.now()}`;
    toast.id = toastId;
    
    const bgColor = {
      error: 'bg-red-500',
      warning: 'bg-yellow-500',
      info: 'bg-blue-500',
      success: 'bg-green-500'
    }[details.type];

    const icon = {
      error: '❌',
      warning: '⚠️',
      info: 'ℹ️',
      success: '✅'
    }[details.type];

    toast.className = `${bgColor} text-white px-6 py-4 rounded-lg shadow-lg max-w-md transform transition-all duration-300 translate-x-full opacity-0`;
    
    toast.innerHTML = `
      <div class="flex items-start space-x-3">
        <span class="text-lg">${icon}</span>
        <div class="flex-1">
          <p class="font-medium">${details.message}</p>
          ${details.action ? `
            <button class="mt-2 text-sm underline hover:no-underline" onclick="document.getElementById('${toastId}').dispatchEvent(new CustomEvent('action'))">
              ${details.action.label}
            </button>
          ` : ''}
        </div>
        <button class="text-white hover:text-gray-200 ml-2" onclick="document.getElementById('${toastId}').dispatchEvent(new CustomEvent('close'))">
          ✕
        </button>
      </div>
    `;

    // Add event listeners
    toast.addEventListener('action', () => {
      if (details.action) {
        details.action.onClick();
      }
    });

    toast.addEventListener('close', () => {
      this.removeToast(toastId);
    });

    this.toastContainer.appendChild(toast);

    // Animate in
    setTimeout(() => {
      toast.classList.remove('translate-x-full', 'opacity-0');
    }, 10);

    // Auto remove
    const duration = details.duration || (details.type === 'error' ? 8000 : 5000);
    setTimeout(() => {
      this.removeToast(toastId);
    }, duration);
  }

  private static removeToast(toastId: string) {
    const toast = document.getElementById(toastId);
    if (toast) {
      toast.classList.add('translate-x-full', 'opacity-0');
      setTimeout(() => {
        toast.remove();
      }, 300);
    }
  }

  // Parse and categorize errors
  static parseError(error: any): ErrorDetails {
    console.error('Error caught by ErrorHandler:', error);

    // Network errors
    if (error.message?.includes('NetworkError') || error.message?.includes('Failed to fetch')) {
      return {
        message: 'Network connection failed. Please check your internet connection and try again.',
        type: 'error',
        action: {
          label: 'Retry',
          onClick: () => window.location.reload()
        }
      };
    }

    // Authentication errors
    if (error.status === 401 || error.message?.includes('401') || error.message?.includes('Authentication')) {
      return {
        message: 'Your session has expired. Please log in again.',
        type: 'warning',
        action: {
          label: 'Login',
          onClick: () => window.location.href = '/login'
        }
      };
    }

    // Authorization errors
    if (error.status === 403 || error.message?.includes('403') || error.message?.includes('Forbidden')) {
      return {
        message: 'You don\'t have permission to perform this action. Please contact your administrator.',
        type: 'warning'
      };
    }

    // Validation errors
    if (error.status === 400 || error.message?.includes('validation') || error.message?.includes('required')) {
      return {
        message: error.message || 'Please check your input and try again.',
        type: 'warning'
      };
    }

    // Server errors
    if (error.status >= 500 || error.message?.includes('500') || error.message?.includes('Internal Server Error')) {
      return {
        message: 'Server error occurred. Our team has been notified. Please try again later.',
        type: 'error',
        action: {
          label: 'Retry',
          onClick: () => window.location.reload()
        }
      };
    }

    // File upload errors
    if (error.message?.includes('upload') || error.message?.includes('file')) {
      return {
        message: 'File upload failed. Please check the file size and format, then try again.',
        type: 'error'
      };
    }

    // TPA/Organization errors
    if (error.message?.includes('TPA') || error.message?.includes('organization')) {
      return {
        message: 'There\'s an issue with your organization settings. Please contact your administrator.',
        type: 'warning'
      };
    }

    // Quote submission errors
    if (error.message?.includes('quote') || error.message?.includes('submission')) {
      return {
        message: 'Quote submission failed. Please review your information and try again.',
        type: 'error'
      };
    }

    // Generic error
    return {
      message: error.message || 'An unexpected error occurred. Please try again.',
      type: 'error'
    };
  }

  // Handle async operations with error handling
  static async handleAsync<T>(
    operation: () => Promise<T>,
    options?: {
      loadingMessage?: string;
      successMessage?: string;
      errorMessage?: string;
      showSuccess?: boolean;
    }
  ): Promise<T | null> {
    try {
      if (options?.loadingMessage) {
        this.showToast({
          message: options.loadingMessage,
          type: 'info',
          duration: 2000
        });
      }

      const result = await operation();

      if (options?.successMessage && options?.showSuccess !== false) {
        this.showToast({
          message: options.successMessage,
          type: 'success'
        });
      }

      return result;
    } catch (error) {
      const errorDetails = this.parseError(error);
      
      if (options?.errorMessage) {
        errorDetails.message = options.errorMessage;
      }

      this.showToast(errorDetails);
      return null;
    }
  }

  // Specific handlers for common operations
  static handleFormSubmission = async (
    submitFn: () => Promise<any>,
    options?: {
      successMessage?: string;
      errorMessage?: string;
    }
  ) => {
    return this.handleAsync(submitFn, {
      loadingMessage: 'Submitting form...',
      successMessage: options?.successMessage || 'Form submitted successfully!',
      errorMessage: options?.errorMessage,
      showSuccess: true
    });
  };

  static handleDataFetch = async (
    fetchFn: () => Promise<any>,
    options?: {
      errorMessage?: string;
      showLoading?: boolean;
    }
  ) => {
    return this.handleAsync(fetchFn, {
      loadingMessage: options?.showLoading ? 'Loading data...' : undefined,
      errorMessage: options?.errorMessage,
      showSuccess: false
    });
  };

  static handleFileUpload = async (
    uploadFn: () => Promise<any>,
    options?: {
      successMessage?: string;
      errorMessage?: string;
    }
  ) => {
    return this.handleAsync(uploadFn, {
      loadingMessage: 'Uploading file...',
      successMessage: options?.successMessage || 'File uploaded successfully!',
      errorMessage: options?.errorMessage || 'File upload failed. Please try again.',
      showSuccess: true
    });
  };

  // Validation helpers
  static validateRequired(value: any, fieldName: string): boolean {
    if (!value || (typeof value === 'string' && !value.trim())) {
      this.showToast({
        message: `${fieldName} is required.`,
        type: 'warning'
      });
      return false;
    }
    return true;
  }

  static validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      this.showToast({
        message: 'Please enter a valid email address.',
        type: 'warning'
      });
      return false;
    }
    return true;
  }

  static validateFileSize(file: File, maxSizeMB: number = 10): boolean {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      this.showToast({
        message: `File size must be less than ${maxSizeMB}MB. Current size: ${(file.size / 1024 / 1024).toFixed(1)}MB`,
        type: 'warning'
      });
      return false;
    }
    return true;
  }

  static validateFileType(file: File, allowedTypes: string[]): boolean {
    if (!allowedTypes.includes(file.type)) {
      this.showToast({
        message: `File type not allowed. Allowed types: ${allowedTypes.join(', ')}`,
        type: 'warning'
      });
      return false;
    }
    return true;
  }

  // Success helpers
  static showSuccess(message: string, action?: { label: string; onClick: () => void }) {
    this.showToast({
      message,
      type: 'success',
      action
    });
  }

  static showWarning(message: string, action?: { label: string; onClick: () => void }) {
    this.showToast({
      message,
      type: 'warning',
      action
    });
  }

  static showInfo(message: string, action?: { label: string; onClick: () => void }) {
    this.showToast({
      message,
      type: 'info',
      action
    });
  }
}

// Initialize on import
if (typeof window !== 'undefined') {
  ErrorHandler.init();
}

export default ErrorHandler; 